import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import styled, { css } from 'styled-components';
import { useDevice } from '../../constants/device/device';
import zIndex from '../../constants/zIndex';

interface StickyProps {
	sticky: boolean;
	stickyVisible?: boolean;
}

const Header = styled.header``;

const Sticky = styled.div<StickyProps>`
	position: ${({ sticky }) => (sticky ? 'fixed' : 'relative')};
	top: 0;
	right: 0;
	left: 0;
	z-index: ${zIndex.header};
	transform: ${({ sticky, stickyVisible }) => sticky && !stickyVisible && `translateY(-100%)`};
	transition: ${({ sticky, stickyVisible }) => sticky && stickyVisible !== undefined && 'transform 0.3s ease-out'};
`;

const StickyPlaceholder = styled.div<{
	sticky: boolean,
	headerHeight: number
}>`
	${({ sticky, headerHeight }) =>
		sticky &&
		headerHeight &&
		css`
			height: ${headerHeight}px;
		`}
`;

interface Props {
	stickyHeaderVisible?: boolean;
	onStickyHeaderVisibilityChange: (state?: boolean) => void;
	onScroll: () => void;
	children: ReactNode;
}

const StickyHeader: FC<Props> = ({ stickyHeaderVisible, onStickyHeaderVisibilityChange, onScroll, children }) => {
	const [stickyHeader, setStickyHeader] = useState(false);
	const [scrollDown, setScrollDown] = useState(false);
	const { ref: inViewRef, inView } = useInView({ threshold: 0 });

	const [headerHeight, setHeaderHeight] = useState(0);
	const headerRef = useRef<HTMLDivElement>(null);

	const { smallDevice } = useDevice();

	useEffect(() => {
		if (headerRef.current) {
			setHeaderHeight(headerRef.current.getBoundingClientRect().height);
		}
	}, [smallDevice]);

	useEffect(() => {
		const threshold = 30;
		let lastScrollY = window.scrollY;
		let ticking = false;

		const updateScrollDir = () => {
			const { scrollY } = window;

			if (Math.abs(scrollY - lastScrollY) < threshold) {
				ticking = false;
				return;
			}
			setScrollDown(scrollY > lastScrollY);
			lastScrollY = scrollY > 0 ? scrollY : 0;
			ticking = false;
			onScroll();
		};

		const handleScroll = () => {
			if (!ticking) {
				window.requestAnimationFrame(updateScrollDir);
				ticking = true;
			}
		};

		window.addEventListener('scroll', handleScroll);
		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, [scrollDown, onScroll]);

	useEffect(() => {
    // When the marker is in view, header is not sticky; otherwise, it is sticky.
    setStickyHeader(!inView);
  }, [inView, setStickyHeader]);

	useEffect(() => {
		onStickyHeaderVisibilityChange(stickyHeader ? !scrollDown : undefined);
	}, [scrollDown, stickyHeader, onStickyHeaderVisibilityChange]);

	return (
		<Header>
			{/* Marker element to observe (you can adjust its placement as needed) */}
			<div ref={inViewRef} style={{ height: 1 }} />
			<Sticky ref={headerRef} sticky={stickyHeader} stickyVisible={stickyHeaderVisible}>
				{children}
			</Sticky>
			<StickyPlaceholder sticky={stickyHeader} headerHeight={headerHeight} />
		</Header>
	);
};

export default StickyHeader;
